import { calculateAppliedAmount, calculateBrokerSplit, findPlan } from 'bundles/backoffice/helpers/plans';
import {
  BUY_SIDE,
  DUAL,
  getTransactionSideDetails,
  newTransactionSideDetails,
  SELL_SIDE,
  UNIT_DOLLARS,
  UNIT_PERCENT,
} from 'bundles/backoffice/vouchers/helpers/transaction_side_details';
import { findPlansAssignedToUser } from 'bundles/backoffice/helpers/plans';
import { getPreSplitDeductionsTotal } from 'bundles/backoffice/vouchers/helpers/deductions';
import {
  getGrossCommissionValues,
} from 'bundles/backoffice/vouchers/helpers/vouchers';
import { round } from 'lodash';

export function parseCommission(plans, commission) {
  // Always look up the plan to get the one serialized with amountByUserId
  const plan = findPlan(plans, {
    id: commission.commissionPlanId,
    user: commission.broker
  });

  return { ...commission, plan };
}

export function updateCommissions(voucher, { accessibleSplitUserIds, transactionSide } = {}) {
  indexCommissions(voucher);

  // Round outside commissions first and independently, as these values affect internal amount calculations
  updateOutsideCommissions(voucher);
  updateInternalCommissions(voucher, transactionSide);
  updateSplitCommissions(voucher, { accessibleSplitUserIds });
}

export function indexCommissions(voucher) {
  voucher.commissions = voucher.commissions.map((commission, index) => ({ ...commission, index }));
}

export function updateInternalCommissions(voucher, transactionSide = null) {
  const { internalCommissions } = partitionCommissions(voucher);

  updateGrossCommissions(voucher, internalCommissions, transactionSide);
}

export function updateOutsideCommissions(voucher) {
  const { outsideCommissions } = partitionCommissions(voucher);

  updateGrossCommissions(voucher, outsideCommissions);
}

export function findCommissionPayables(voucher, commission) {
  return voucher.payables.filter(payable => payable.commissionId === commission.id);
}

export function updateSplitCommissions(voucher, { accessibleSplitUserIds } = {}) {
  for (const commission of voucher.commissions) {
    const updateSplit = canAccessSplitFor(commission.broker, accessibleSplitUserIds);
    updateSplitCommission(commission, updateSplit, findCommissionPayables(voucher, commission));
  }
}

export function updateHouseDeductionAmounts(voucher) {
  let remainder = 0;

  for (const commission of voucher.commissions) {
    if (!commission._destroy && !commission.outside) {
      const proportionalAmount = voucher.totalHouseDeduction * commission.grossPercent / 100;
      commission.houseDeductionAmount = round(proportionalAmount, 2);
      remainder += proportionalAmount - commission.houseDeductionAmount;
    }
  }

  const lastCommission = voucher.commissions[voucher.commissions.length - 1];
  if (lastCommission) {
    lastCommission.houseDeductionAmount = round(lastCommission.houseDeductionAmount + remainder, 2);
  }
}

function updateGrossCommissions(voucher, commissions) {
  const percentCommissions = [];

  for (const commission of commissions) {
    updateGrossCommission(voucher, commission);

    if (commission.grossUnit === 'gross_unit_percent') {
      percentCommissions.push(commission);
    }
  }

  if (voucher.hasTransactionSides) {
    roundTransactionSideCommissionAmounts(commissions);
  }

  if (percentCommissions.length === 0) {
    // Only modify amounts calculated from percents, never those amounts provided directly by the user
    return;
  }

  roundCommissionAmounts(percentCommissions);
}

function partitionCommissions(voucher) {
  const internalCommissions = [];
  const outsideCommissions = [];

  for (const commission of voucher.commissions) {
    if (commission.outside) {
      outsideCommissions.push(commission);
    } else {
      internalCommissions.push(commission);
    }
  }

  return { internalCommissions, outsideCommissions };
}

function roundAmount(amount) {
  return round(amount, 2);
}

export function updateCommissionByTransactionSides(voucher, commission) {
  if (!voucher.hasTransactionSides) return;

  const availableGrossCommission = {
    [BUY_SIDE]: getAvailableGrossCommission(voucher, commission, BUY_SIDE),
    [SELL_SIDE]: getAvailableGrossCommission(voucher, commission, SELL_SIDE),
  };

  commission.transactionSideDetails = commission.transactionSideDetails.map((details) => {
    return updateByUnit(details, availableGrossCommission[details.transactionSide], UNIT_DOLLARS);
  });

  commission.grossAmount = commission.transactionSideDetails.reduce(
    (sum, details) => sum + details.grossAmount, 0
  );
  commission.grossUnit = 'gross_unit_dollars';
}

function roundTransactionSideCommissionAmounts(commissions) {
  const buySidePercentCommissions = [];
  const sellSidePercentCommissions = [];

  for (const commission of commissions) {
    if ((commission.transactionSide === BUY_SIDE || commission.transactionSide === DUAL) &&
      getTransactionSideDetails(commission, BUY_SIDE)?.grossUnit === UNIT_PERCENT) {
      buySidePercentCommissions.push(commission);
    }

    if ((commission.transactionSide === SELL_SIDE || commission.transactionSide === DUAL) &&
      getTransactionSideDetails(commission, SELL_SIDE)?.grossUnit === UNIT_PERCENT) {
      sellSidePercentCommissions.push(commission);
    }
  }

  if (buySidePercentCommissions.length > 0) {
    roundCommissionAmounts(buySidePercentCommissions, BUY_SIDE);
  }

  if (sellSidePercentCommissions.length > 0) {
    roundCommissionAmounts(sellSidePercentCommissions, SELL_SIDE);
  }
}

function roundCommissionAmounts(percentCommissions, transactionSide = null) {
  let roundedTotal = 0;
  let unroundedTotal = 0;
  for (const commission of percentCommissions) {
    const grossAmount = getAmount(commission, transactionSide);
    const roundedAmount = roundAmount(grossAmount);
    setAmount(commission, roundedAmount, transactionSide);
    if (transactionSide) {
      // Update commission.grossAmount to reflect the change in the transaction side
      updateCommissionAmountFromTransactionSides(commission);
    }
    unroundedTotal += grossAmount;
    roundedTotal += roundedAmount;
  }

  const offset = roundAmount(unroundedTotal - roundedTotal);
  if (offset !== 0) {
    setAmount(
      percentCommissions[0],
      roundAmount(
        getAmount(percentCommissions[0], transactionSide) + offset
      ),
      transactionSide
    );

    if (transactionSide) {
      // Update commission.grossAmount to reflect the change in the transaction side
      updateCommissionAmountFromTransactionSides(percentCommissions[0]);
    }
  }
}

function updateCommissionAmountFromTransactionSides(commission) {
  setAmount(
    commission,
    commission.transactionSideDetails.reduce(
      (sum, details) => sum + details.grossAmount, 0
    )
  );
}

export function updateGrossCommission(voucher, commission) {
  updateCommissionByTransactionSides(voucher, commission);

  const availableGrossCommission = getAvailableGrossCommission(voucher, commission);

  return updateByUnit(commission, availableGrossCommission, 'gross_unit_dollars');
}

function updateByUnit(record, grossAmount, dollarUnit) {
  if (record.grossUnit === dollarUnit) {
    if (grossAmount) {
      record.grossPercent = record.grossAmount / grossAmount * 100;
    } else {
      record.grossPercent = 0;
    }
  } else {
    record.grossPercent = Math.max(0.0, Math.min(100.0, record.grossPercent));
    record.grossAmount = record.grossPercent * grossAmount / 100;
  }

  return record;
}

export function updateSplitCommission(commission, updateSplits, payables) {
  if (commission.outside) {
    // Broker/house split isn't really a thing for outside commissions, but keep the columns in sync
    // with the gross columns just to simplify usage.
    commission.brokerSplitAmount = commission.grossAmount;
    commission.brokerSplitPercent = 100;
  } else if (updateSplits) {
    updateInternalCommissionSplit(commission, payables);
  } else {
    commission.appliedPlanTiers = [];
  }

  return commission;
}

function getAvailableGrossCommission(voucher, commission, transactionSide = null) {
  const grossCommissionAmount = getGrossCommissionValues(voucher, transactionSide).grossAmount;
  if (commission.outside) {
    return grossCommissionAmount;
  }

  const outsideCommissionAmount = voucher.commissions.reduce((sum, commission) => {
    if (commission.outside && !commission._destroy) {
      sum += getAmount(commission, transactionSide);
    }

    return sum;
  }, 0);

  const deductionTotal = getPreSplitDeductionsTotal(voucher, transactionSide);

  return grossCommissionAmount - deductionTotal - outsideCommissionAmount;
}

function updateInternalCommissionSplit(commission, payables) {
  const { broker, customCommissionPlanAmount, grossAmount, plan } = commission;

  if (plan && !commission.planSplitOverridden) {
    const calculation = calculateBrokerSplit({
      plan,
      startingPlanAmount: plan.amountByUserId[broker.id],
      grossAmount,
      amountToApply: calculateAppliedAmount(plan, customCommissionPlanAmount, grossAmount),
      payables,
    });

    if (plan.forCohort && calculation.appliedTiers.length) {
      // Cohort plans are weird and can't be estimated in the UI, so just show an estimate using the calculations
      // to get the current tier and apply it to the commission gross amount
      const brokerSplitPercent = calculation.appliedTiers[0].brokerSplit;
      commission.brokerSplitPercent = brokerSplitPercent;
      commission.brokerSplitAmount = commission.grossAmount * brokerSplitPercent / 100;
    } else {
      commission.brokerSplitAmount = calculation.amount;
      commission.brokerSplitPercent = calculation.percent;
    }

    commission.appliedPlanTiers = calculation.appliedTiers;

    if (calculation.customPlanAmount != null) {
      commission.customCommissionPlanAmount = calculation.customPlanAmount;
    }
  } else if (commission.splitUnit === 'split_unit_dollars') {
    if (commission.grossAmount) {
      commission.brokerSplitPercent = commission.brokerSplitAmount / commission.grossAmount * 100;
    } else {
      commission.brokerSplitPercent = 0;
    }
  } else {
    commission.brokerSplitPercent = Math.max(0.0, Math.min(100.0, commission.brokerSplitPercent));
    commission.brokerSplitAmount = commission.brokerSplitPercent * commission.grossAmount / 100;
  }

  if (plan && commission.planSplitOverridden && plan.customAmountMode === 'house_split') {
    commission.customCommissionPlanAmount = commission.grossAmount  - commission.brokerSplitAmount;
  }
}

export function buildBlankCommission({ broker, contact, includeSplit, plans = [] }) {
  let commission = {
    broker,
    contact,
    deductions: [],
    grossAmount: 0,
    grossPercent: 0,
    grossUnit: 'gross_unit_percent',
    plan: plans.find(plan => plan.id === broker?.primaryCommissionPlanId),
    splitUnit: 'split_unit_percent',
    totalDeduction: 0
  };

  if (includeSplit) {
    commission = { ...commission, brokerSplitAmount: 0, brokerSplitPercent: 100.0 };
  }

  return commission;
}

export function buildBlankTransactionSideDetails(commission) {
  const transactionSideDetails = [];
  if ([BUY_SIDE, DUAL].includes(commission.transactionSide)) {
    transactionSideDetails.push(newTransactionSideDetails(BUY_SIDE));
  }
  if ([SELL_SIDE, DUAL].includes(commission.transactionSide)) {
    transactionSideDetails.push(newTransactionSideDetails(SELL_SIDE));
  }
  return transactionSideDetails;
}

export function isFullyPaid(commission, voucher) {
  const grossPaidAmount = voucher.payables
    .filter(payable => payable.commissionId === commission.id)
    .reduce((payablesPayments, payable) => [...payablesPayments, ...payable.payablesPayments], [])
    .reduce((sum, { grossAmount }) => sum + grossAmount, 0);

  return commission.grossAmount === grossPaidAmount;
}

export function canAccessSplitFor(user, accessibleSplitUserIds) {
  if (accessibleSplitUserIds) {
    return user && accessibleSplitUserIds.includes(user.id);
  } else {
    return true;
  }
}

export function canHaveCustomPlanAmount(commission) {
  return (
    commission.plan && commission.plan.calculationMode === 'use_custom_commission_amount'
    // The calculated plan amount from house split plans is not right for cohort plans, so it must be hidden
      && !(commission.plan.customAmountMode === 'house_split' && commission.plan.forCohort)
  );
}

export function getAmount(commission, transactionSide = null) {
  if (!transactionSide || !commission.transactionSideDetails) {
    return commission.grossAmount;
  }

  return commission.transactionSideDetails.find(
    details => details.transactionSide === transactionSide && !details._destroy
  )?.grossAmount || 0;
}

export function setAmount(commission, grossAmount, transactionSide = null) {
  if (!transactionSide) {
    commission.grossAmount = grossAmount;
    return;
  }

  const details = getTransactionSideDetails(commission, transactionSide);

  details.grossAmount = grossAmount;
}

export function getPercent(commission, transactionSide = null) {
  if (!transactionSide || !commission.transactionSideDetails) {
    return commission.grossPercent;
  }

  return commission.transactionSideDetails.find(
    details => details.transactionSide === transactionSide && !details._destroy
  )?.grossPercent || 0;
}

export function filterCommissions(commissions, transactionSide = null, outside = false) {
  return commissions.filter(commission => (
    !!commission.outside === outside
      && !commission._destroy
      && (!transactionSide || [transactionSide, DUAL].includes(commission.transactionSide))
  ));
}

export function getGrossPaid(commission, payables, transactionSide = null) {
  const grossPaid = payables.reduce((sum, payable) => {
    return sum + (payable.commissionId === commission.id ? payable.grossPaid : 0);
  }, 0);

  if (!transactionSide || commission.transactionSide !== DUAL) {
    return grossPaid;
  }

  return grossPaid * getAmount(commission, transactionSide) / commission.grossAmount;
}

export function updateTransactionSide(voucher, commission, newTransactionSide) {
  if (commission.transactionSide === newTransactionSide) {
    return commission;
  }

  const transactionSideDetails = [];

  if (newTransactionSide === DUAL) {
    [BUY_SIDE, SELL_SIDE].forEach((transactionSide) => {
      const detail = commission.transactionSideDetails.find(details => details.transactionSide === transactionSide);
      if (detail) {
        delete detail._destroy;
        transactionSideDetails.push(detail);
      } else {
        transactionSideDetails.push({
          transactionSide,
          grossAmount: 0,
          grossPercent: 0,
          grossUnit: UNIT_DOLLARS,
        });
      }
    });
  } else if (commission.transactionSide == DUAL) {
    transactionSideDetails.push(...commission.transactionSideDetails.map((details) => {
      if (details.transactionSide === newTransactionSide) {
        return details;
      }
      return { ...details, _destroy: true };
    }));
  } else {
    const currentTransactionSideDetail = (
      commission.transactionSideDetails.find(details => details.transactionSide === commission.transactionSide)
    );

    const newTransactionSideDetail = (
      commission.transactionSideDetails.find(details => details.transactionSide === newTransactionSide)
    );

    if (!newTransactionSideDetail) {
      transactionSideDetails.push({ ...currentTransactionSideDetail, transactionSide: newTransactionSide, id: null });
    } else {
      delete newTransactionSideDetail._destroy;
      transactionSideDetails.push(newTransactionSideDetail);
    }

    transactionSideDetails.push({ ...currentTransactionSideDetail, _destroy: true });
  }

  const updatedCommission = { ...commission, transactionSide: newTransactionSide, transactionSideDetails };
  updateCommissionByTransactionSides(voucher, updatedCommission);

  return updatedCommission;
}

export function hasMatchingPayee({ source, target }) {
  if (target._destroy) {
    return false;
  } else if (source.broker) {
    return source.broker.id === target.broker?.id;
  } else if (source.contact) {
    return source.contact.id === target.contact?.id
      && source.contact.contactType === target.contact?.contactType;
  }
}

export function needsPlanConfirmation(commission, plans) {
  if (!commission.plan) {
    return false;
  }

  const assignedPlans = findPlansAssignedToUser(plans, commission.broker);
  return !assignedPlans.find(plan => plan.id === commission.plan.id);
}
